/**
 * Catálogo de permissões do LD Gestão.
 *
 * Fonte única de verdade para API e frontends. A API sincroniza este catálogo
 * com a tabela `Permission` na inicialização e no seed. Uma permissão é
 * identificada por `modulo:acao` (ex.: `users:create`).
 */
export const PERMISSION_ACTIONS = [
    'read',
    'create',
    'update',
    'delete',
    'approve',
    'export',
    'manage',
];
export const PERMISSION_ACTION_LABELS = {
    read: 'Visualizar',
    create: 'Criar',
    update: 'Editar',
    delete: 'Excluir / desativar',
    approve: 'Aprovar',
    export: 'Exportar',
    manage: 'Gerenciar configurações',
};
const CRUD = ['read', 'create', 'update', 'delete'];
const CRUD_EXPORT = [...CRUD, 'export'];
const CRUD_APPROVE_EXPORT = [...CRUD, 'approve', 'export'];
export const PERMISSION_MODULES = [
    {
        code: 'dashboard',
        label: 'Dashboard',
        description: 'Indicadores gerais da empresa',
        actions: ['read'],
        phase: 1,
    },
    {
        code: 'settings',
        label: 'Configurações da empresa',
        description: 'Dados cadastrais, parâmetros e numeração',
        actions: ['read', 'manage'],
        phase: 1,
    },
    {
        code: 'users',
        label: 'Usuários',
        description: 'Usuários e acessos da empresa',
        actions: [...CRUD],
        phase: 1,
    },
    {
        code: 'roles',
        label: 'Perfis de acesso',
        description: 'Perfis e permissões',
        actions: [...CRUD],
        phase: 1,
    },
    {
        code: 'audit',
        label: 'Auditoria',
        description: 'Logs de auditoria',
        actions: ['read', 'export'],
        phase: 1,
    },
    {
        code: 'products',
        label: 'Produtos',
        description: 'Catálogo de produtos',
        actions: CRUD_EXPORT,
        phase: 2,
    },
    {
        code: 'materials',
        label: 'Materiais',
        description: 'Cadastro de materiais e insumos',
        actions: CRUD_EXPORT,
        phase: 2,
    },
    {
        code: 'stock',
        label: 'Estoque',
        description: 'Movimentações, ajustes e inventário',
        actions: [...CRUD, 'approve', 'export', 'manage'],
        phase: 2,
    },
    {
        code: 'customers',
        label: 'Clientes',
        description: 'Cadastro de clientes',
        actions: CRUD_EXPORT,
        phase: 2,
    },
    {
        code: 'suppliers',
        label: 'Fornecedores',
        description: 'Cadastro de fornecedores',
        actions: CRUD_EXPORT,
        phase: 2,
    },
    {
        code: 'sales',
        label: 'Vendas',
        description: 'Orçamentos e pedidos de venda',
        actions: CRUD_APPROVE_EXPORT,
        phase: 3,
    },
    {
        code: 'purchases',
        label: 'Compras',
        description: 'Solicitações e pedidos de compra',
        actions: CRUD_APPROVE_EXPORT,
        phase: 3,
    },
    {
        code: 'production',
        label: 'Produção',
        description: 'Ordens de produção e apontamentos',
        actions: CRUD_APPROVE_EXPORT,
        phase: 3,
    },
    {
        code: 'quality',
        label: 'Qualidade',
        description: 'Inspeções e tratamentos',
        actions: CRUD_EXPORT,
        phase: 3,
    },
    {
        code: 'financial',
        label: 'Financeiro',
        description: 'Contas, títulos e liquidações',
        actions: [...CRUD, 'approve', 'export', 'manage'],
        phase: 4,
    },
    {
        code: 'hr',
        label: 'Recursos Humanos',
        description: 'Colaboradores e gestão de RH',
        actions: CRUD_EXPORT,
        phase: 5,
    },
    {
        code: 'hr_sensitive',
        label: 'RH — dados sensíveis',
        description: 'Salários, dados bancários e pessoais',
        actions: ['read', 'update', 'export'],
        phase: 5,
    },
    {
        code: 'assets',
        label: 'Patrimônio',
        description: 'Equipamentos e veículos',
        actions: CRUD_EXPORT,
        phase: 5,
    },
    {
        code: 'maintenance',
        label: 'Manutenções',
        description: 'Planos e ordens de manutenção',
        actions: CRUD_APPROVE_EXPORT,
        phase: 5,
    },
    {
        code: 'machines',
        label: 'Horas de máquina',
        description: 'Apontamentos de uso de máquinas, combustível e faturas (approve = emitir/cancelar faturas)',
        actions: CRUD_APPROVE_EXPORT,
        phase: 9,
    },
    {
        code: 'waste',
        label: 'Resíduos (MTR)',
        description: 'Manifestos de transporte de resíduos (MTR) e certificados de destinação (CDF)',
        actions: CRUD_EXPORT,
        phase: 9,
    },
    {
        code: 'invoices',
        label: 'Notas fiscais',
        description: 'Notas de entrada e saída',
        actions: CRUD_EXPORT,
        phase: 6,
    },
    {
        code: 'documents',
        label: 'Documentos',
        description: 'Gestão documental (manage = documentos confidenciais)',
        actions: [...CRUD, 'export', 'manage'],
        phase: 6,
    },
    {
        code: 'logistics',
        label: 'Logística',
        description: 'Expedição e entregas',
        actions: CRUD_APPROVE_EXPORT,
        phase: 6,
    },
    {
        code: 'reports',
        label: 'Relatórios',
        description: 'Central de relatórios',
        actions: ['read', 'export'],
        phase: 7,
    },
];
/** Lista plana de todas as permissões conhecidas. */
export const PERMISSIONS = PERMISSION_MODULES.flatMap((mod) => mod.actions.map((action) => ({
    code: `${mod.code}:${action}`,
    module: mod.code,
    action,
    label: `${mod.label} — ${PERMISSION_ACTION_LABELS[action]}`,
    description: mod.description,
})));
export const PERMISSION_CODES = PERMISSIONS.map((p) => p.code);
const PERMISSION_CODE_SET = new Set(PERMISSION_CODES);
export function isPermissionCode(value) {
    return PERMISSION_CODE_SET.has(value);
}
export function permissionsForModule(module) {
    return PERMISSIONS.filter((p) => p.module === module).map((p) => p.code);
}
//# sourceMappingURL=permissions.js.map